import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type SyncJobStatus = 'idle' | 'pending' | 'success' | 'error';

export interface SyncJobMetadata {
  readonly entity?: string;
  readonly scope?: string;
  readonly source?: string;
}

export interface SyncJobDiagnostics {
  readonly key: string;
  readonly status: SyncJobStatus;
  readonly attempts: number;
  readonly lastStartedAt?: number;
  readonly lastFinishedAt?: number;
  readonly lastSuccessAt?: number;
  readonly lastErrorAt?: number;
  readonly lastError?: string;
  readonly nextRetryAt?: number;
  readonly metadata?: SyncJobMetadata;
}

export type RealtimeChannelStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface RealtimeChannelDiagnostics {
  readonly key: string;
  readonly status: RealtimeChannelStatus;
  readonly lastChangedAt: number;
  readonly lastError?: string;
}

interface RealtimeSyncDiagnosticsState {
  readonly jobs: Record<string, SyncJobDiagnostics>;
  readonly channels: Record<string, RealtimeChannelDiagnostics>;
  readonly upsertJob: (diagnostics: SyncJobDiagnostics) => void;
  readonly removeJob: (key: string) => void;
  readonly setChannelStatus: (
    key: string,
    status: RealtimeChannelStatus,
    options?: { error?: string },
  ) => void;
  readonly clearChannels: () => void;
  readonly reset: () => void;
}

export const useRealtimeSyncDiagnosticsStore = create<RealtimeSyncDiagnosticsState>()(
  devtools(
    (set) => ({
      jobs: {},
      channels: {},
      upsertJob: (diagnostics) => {
        set((state) => ({
          jobs: {
            ...state.jobs,
            [diagnostics.key]: diagnostics,
          },
        }));
      },
      removeJob: (key) => {
        set((state) => {
          const next = { ...state.jobs };
          delete next[key];
          return { jobs: next, channels: state.channels };
        });
      },
      setChannelStatus: (key, status, options) => {
        set((state) => ({
          channels: {
            ...state.channels,
            [key]: {
              key,
              status,
              lastChangedAt: Date.now(),
              lastError: options?.error,
            },
          },
        }));
      },
      clearChannels: () => {
        set((state) => ({
          jobs: state.jobs,
          channels: {},
        }));
      },
      reset: () => {
        set({ jobs: {}, channels: {} });
      },
    }),
    { name: 'RealtimeSyncDiagnosticsStore' },
  ),
);

export const getRealtimeSyncDiagnostics = () => useRealtimeSyncDiagnosticsStore.getState().jobs;
export const getRealtimeChannelDiagnostics = () =>
  useRealtimeSyncDiagnosticsStore.getState().channels;

const selectRealtimeSyncJobs = (
  state: RealtimeSyncDiagnosticsState,
): Record<string, SyncJobDiagnostics> => state.jobs;

const selectRealtimeChannelStatuses = (
  state: RealtimeSyncDiagnosticsState,
): Record<string, RealtimeChannelDiagnostics> => state.channels;

export function useRealtimeSyncJobMap(): Record<string, SyncJobDiagnostics> {
  return useRealtimeSyncDiagnosticsStore(selectRealtimeSyncJobs);
}

export function useRealtimeChannelMap(): Record<string, RealtimeChannelDiagnostics> {
  return useRealtimeSyncDiagnosticsStore(selectRealtimeChannelStatuses);
}
